/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Group, Layer, Stage, Transformer } from 'react-konva';
import { Img } from './components/Img';
import type { ImgProps, LayerChangeObject } from './types.d';
import { MainContainer } from './components/MainContainer';
import { layerClippingHandler } from './utils';
import { StaticLayersOnly } from './components/StaticLayersOnly';
import { KonvaEventObject } from 'konva/lib/Node';
import Konva from 'konva';
import { Vector2d } from 'konva/lib/types';

const ProductWorkbenchCanvas = ({
    dimensions,
    layers = [],
    onChange,
    canvasSizeByLayerId,
    onLoad
}: {
    dimensions: { width: number; height: number };
    layers: ImgProps[];
    onChange: (changes: LayerChangeObject[]) => void;
    canvasSizeByLayerId: string;
    onLoad?: (stage?: typeof Stage) => void;
}) => {
    const layersById = useMemo(
        () => layers.reduce((byId, layer) => ({ ...byId, [layer.id]: layer }), {}),
        [layers]
    );
    const [selectedLayerId, setSelectedLayerId] = useState<string | number | undefined>('');
    const [loadedImagesById, setLoadedImagesById] = useState<{ [key: string]: HTMLImageElement }>(
        {}
    );

    const [isLoaded, setIsLoaded] = useState(false);
    const stageRef = React.useRef<any>();
    const shapeRef = React.useRef<any>();
    const trRef = React.useRef<any>();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [layerInstancesById, setLayerInstancesById] = useState<{ [key: string]: Konva.Image }>(
        {}
    );
    /**
     * 'canvasToImageWidthRatio' is really important. It needs to have a reference to some layer image that
     * covers the whole canvas. The default is set to an arbitrary 0.5 (50%) value b/c then canvas becomes roughly
     * 1/2 the size of a 1600x1000 size image.
     */
    const canvasToImageWidthRatio = useMemo(() => {
        if (!canvasSizeByLayerId || !loadedImagesById[canvasSizeByLayerId]) {
            return 0.5;
        }
        return dimensions?.width / loadedImagesById[canvasSizeByLayerId]?.width;
    }, [dimensions, loadedImagesById]);

    /**
     * deselect editable layer so the editing "box" disappears from around the image.
     */
    const deselect = useCallback(() => {
        shapeRef.current = null;
        setSelectedLayerId('');
    }, [shapeRef.current, setSelectedLayerId]);

    /**
     * track what image was loaded into memory. It should match the layers passed in. This helps with
     * knowing that all the images/layers are loaded
     */
    const handleImgLoad = useCallback(
        (id: string, img: HTMLImageElement | undefined) => {
            if (!img) return;

            setLoadedImagesById((prev) => ({
                ...prev,
                [id]: img
            }));
        },
        [setLoadedImagesById]
    );

    const onClickTapHandler = useCallback(
        (layer: Partial<ImgProps>) => {
            return (e: any) => {
                deselect();
                if (layer.draggable && layer.id) {
                    shapeRef.current = e.target;
                    setSelectedLayerId(layer.id);
                }
            };
        },
        [shapeRef.current, setSelectedLayerId]
    );

    useEffect(() => {
        if (loadedImagesById) {
            if (onLoad && layers.every((l) => Boolean(loadedImagesById[l.id]))) {
                onLoad(stageRef.current);
                setIsLoaded(true);
            }
        }
    }, [loadedImagesById]);

    useEffect(() => {
        if (selectedLayerId && shapeRef.current) {
            trRef.current?.nodes([shapeRef.current]);
            trRef.current?.getLayer().batchDraw();
        }
    }, [selectedLayerId]);

    // @ts-ignore
    return (
        <MainContainer className={isLoaded ? '_loaded' : ''}>
            <Stage
                ref={(ref) => {
                    stageRef.current = ref;
                }}
                y={dimensions?.height / 2 - 600 * canvasToImageWidthRatio}
                title="iem preview"
                width={dimensions?.width}
                height={dimensions?.height}
                scale={{ x: canvasToImageWidthRatio, y: canvasToImageWidthRatio }}
                onMouseLeave={deselect}>
                <Layer>
                    <StaticLayersOnly
                        layers={layers}
                        deselect={deselect}
                        handleImgLoad={handleImgLoad}
                    />
                    {layers
                        .filter((layer) => layer.draggable)
                        .map(({ src, clipOrigin = [0, 0], clip, modifier, ...layer }: ImgProps) => (
                            <Group
                                key={layer.id}
                                displayName="Editable layer"
                                clipFunc={
                                    clip ? layerClippingHandler(clip, clipOrigin, layer) : undefined
                                }>
                                <Img
                                    src={src}
                                    clipOrigin={clipOrigin}
                                    {...layer}
                                    onLoad={(img) => {
                                        handleImgLoad(layer.id, img);
                                    }}
                                    onPosInitialized={(layerChange, konvaImage) => {
                                        layerChange.offsetX = konvaImage?.attrs.width / 2;
                                        layerChange.offsetY = konvaImage?.attrs.height / 2;
                                        onChange([layerChange]);
                                        if (konvaImage) {
                                            setLayerInstancesById((old) => ({
                                                ...old,
                                                [layer.id]: konvaImage
                                            }));
                                        }
                                    }}
                                    onClick={onClickTapHandler(layer)}
                                    onTap={onClickTapHandler(layer)}
                                    onDragMove={(e: KonvaEventObject<Event>) => {
                                        // console.log(e);
                                        const controller = e.target;
                                        const controllerPos = controller.getPosition();
                                        const xDiff = controllerPos.x - layer.clipCenter[0];
                                        const yDiff = controllerPos.y - layer.clipCenter[1];
                                        if (
                                            modifier?.mirrored?.enabled &&
                                            layersById[modifier?.mirrored?.target].draggable
                                        ) {
                                            const targetLayerId = modifier?.mirrored?.target;
                                            const affectedLayer = layersById[targetLayerId];
                                            const affectedImg = layerInstancesById[targetLayerId];
                                            affectedImg.setPosition({
                                                x: affectedLayer.clipCenter[0] - xDiff,
                                                y: affectedLayer.clipCenter[1] + yDiff
                                            } as Vector2d);
                                        } else {
                                            if (
                                                modifier?.both?.enabled &&
                                                layersById[modifier?.both?.target].draggable
                                            ) {
                                                const targetLayerId = modifier?.both?.target;
                                                const affectedLayer = layersById[targetLayerId];
                                                const affectedImg =
                                                    layerInstancesById[targetLayerId];
                                                affectedImg.setPosition({
                                                    x: affectedLayer.clipCenter[0] + xDiff,
                                                    y: affectedLayer.clipCenter[1] + yDiff
                                                } as Vector2d);
                                            }
                                        }
                                    }}
                                    onDragEnd={(e) => {
                                        // @ts-ignore
                                        let layersChanged = [e];
                                        const modified = modifier?.both?.target;
                                        if (
                                            modified &&
                                            layerInstancesById[modified] &&
                                            layersById[modified].draggable
                                        ) {
                                            layersChanged.push({
                                                id: modified,
                                                ...layerInstancesById[modified].attrs
                                            } as LayerChangeObject);
                                        }
                                        onChange(layersChanged);
                                    }}
                                    onTransform={(e: KonvaEventObject<Event>) => {
                                        const controller = e.target;
                                        if (
                                            modifier?.mirrored?.enabled &&
                                            layersById[modifier?.mirrored?.target].draggable
                                        ) {
                                            const targetLayerId = modifier?.mirrored?.target;
                                            const affectedImg = layerInstancesById[targetLayerId];
                                            affectedImg.rotation(360 - controller.attrs.rotation);
                                            affectedImg.scaleX(-1 * controller.attrs.scaleX);
                                            affectedImg.scaleY(controller.attrs.scaleY);
                                            // console.log(controller);
                                        } else {
                                            if (
                                                modifier?.both?.enabled &&
                                                layersById[modifier?.both?.target].draggable
                                            ) {
                                                const targetLayerId = modifier?.both?.target;
                                                const affectedImg =
                                                    layerInstancesById[targetLayerId];
                                                affectedImg.rotation(
                                                    360 - controller.attrs.rotation
                                                );
                                                affectedImg.scaleX(controller.attrs.scaleX);
                                                affectedImg.scaleY(controller.attrs.scaleY);
                                            }
                                        }
                                    }}
                                    onTransformEnd={(e) => {
                                        let layersChanged = [e];
                                        const modified = modifier?.both?.target;
                                        if (
                                            modified &&
                                            layerInstancesById[modified] &&
                                            layersById[modified].draggable
                                        ) {
                                            layersChanged.push({
                                                id: modified,
                                                ...layerInstancesById[modified].attrs
                                            } as LayerChangeObject);
                                        }
                                        onChange(layersChanged);
                                    }}
                                />
                            </Group>
                        ))}
                    {selectedLayerId && (
                        <Transformer
                            ref={trRef}
                            centeredScaling
                            borderStroke="black"
                            anchorStroke="black"
                            anchorFill="white"
                            anchorCornerRadius={2}
                            borderDash={[3, 8]}
                            shouldOverdrawWholeArea
                            enabledAnchors={[
                                'top-left',
                                'top-right',
                                'bottom-left',
                                'bottom-right'
                            ]}
                            boundBoxFunc={(oldBox, newBox) => {
                                const stageWidth = dimensions.width / canvasToImageWidthRatio;
                                const stageHeight =
                                    (dimensions.height - 50) / canvasToImageWidthRatio;
                                if (
                                    newBox.x < 0 ||
                                    newBox.y < 0 ||
                                    newBox.x + newBox.width > stageWidth ||
                                    newBox.y + newBox.height > stageHeight
                                ) {
                                    return oldBox;
                                }
                                return newBox;
                            }}
                        />
                    )}
                </Layer>
            </Stage>
        </MainContainer>
    );
};

export default ProductWorkbenchCanvas;
